import { releaseChannel } from "@/lib/release-channel";
/** Metadata-only timing for a locally driven onboarding diagnostic run. */
export interface LatencyFields {
  presentationId?: string;
  stepId?: string;
  displayId?: number | null;
  targetDisplayId?: number | null;
  ownerEpoch?: number;
  operationId?: string;
  executionId?: string;
  clientRequestId?: string;
  recordingId?: string | null;
  imageId?: string;
  imageIds?: string[];
  /** Frame ids, or `id=owner` entries of what a presentation showed. */
  frames?: string[];
  before?: string[];
  after?: string[];
  bytes?: number;
  count?: number;
  scope?: string;
  quality?: string;
  outcome?: string;
  phase?: string;
  durationMs?: number;
  timeoutMs?: number;
  timedOut?: boolean;
}

export function sessionLatencyEnabled(): boolean {
  return (
    releaseChannel() === "local" ||
    import.meta.env.VITE_EXECUTION_TRACE === "1" ||
    (globalThis as Record<string, unknown>).__DENKER_TRACE__ === true
  );
}

export function recordSessionLatency(
  event: string,
  fields: LatencyFields = {},
): void {
  if (!sessionLatencyEnabled()) return;
  // Do not pass prompts, filenames, image contents, URLs, or error messages.
  // The native dev console bridge persists this line to the local desktop log.
  console.info(
    "session_latency",
    JSON.stringify({
      ...fields,
      event,
      ts: Date.now() / 1000,
    }),
  );
}

export function startLatencySpan(event: string, fields: LatencyFields = {}) {
  if (!sessionLatencyEnabled()) return (_fields?: LatencyFields) => {};
  const operationId = fields.operationId ?? crypto.randomUUID();
  const startedAt = performance.now();
  recordSessionLatency(`${event}.started`, { ...fields, operationId });
  let finished = false;
  return (result: LatencyFields = {}): void => {
    if (finished) return;
    finished = true;
    recordSessionLatency(`${event}.finished`, {
      ...fields,
      ...result,
      operationId,
      durationMs: Math.round(performance.now() - startedAt),
    });
  };
}

export function traceDispatchReceipt(
  accepted: Promise<boolean>,
  clientRequestId: string,
): void {
  if (!sessionLatencyEnabled()) return;
  void accepted.then(
    (ok) =>
      recordSessionLatency("dispatch.receipt", {
        clientRequestId,
        outcome: ok ? "accepted" : "rejected",
      }),
    () =>
      recordSessionLatency("dispatch.receipt", {
        clientRequestId,
        outcome: "error",
      }),
  );
}
